import { ENV, AXON_EDC_URL } from '@env';
import { TranslocoService, AvailableLangs } from '@ngneat/transloco';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { SnCurrentUser } from '@lib/auth';
interface EvidenceSessionData {
  data?: {
    user?: {
      attributes?: {
        language?: string;
      };
    };
    agency?: {
      attributes?: {
        language?: string;
      };
    };
  };
}
@Injectable({
  providedIn: 'root'
})
export class LanguageSelectionService {
  private readonly LANGUAGE_CACHE_KEY = 'fusus_user_language';
  private _languageInitialized = false;
  private _currentUserData: SnCurrentUser;
  
  private readonly _currentLanguage$: BehaviorSubject<string>;
  public readonly language$: Observable<string>;

  constructor(
    private http: HttpClient,
    private transloco: TranslocoService,
  ) {
    const defaultLang = this.transloco.getDefaultLang();
    this._currentLanguage$ = new BehaviorSubject<string>(defaultLang);
    this.language$ = this._currentLanguage$.asObservable().pipe(distinctUntilChanged());
  }

  initialize(currentUserData: SnCurrentUser): Observable<string> {
    this._currentUserData = currentUserData;
    const defaultLang = this.transloco.getDefaultLang();

    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang');
    if (urlLang) {
      this._languageInitialized = true;
      sessionStorage.setItem(this.LANGUAGE_CACHE_KEY, urlLang);
      
      this._currentLanguage$.next(urlLang);
      if (this.isValidLanguage(urlLang)) {
        this.transloco.setActiveLang(urlLang);
      } else {
        this.transloco.setActiveLang(defaultLang);
      }
      
      return of(urlLang);
    }

    if (this._languageInitialized) {
      const cachedLang = sessionStorage.getItem(this.LANGUAGE_CACHE_KEY) || defaultLang;
      
      this._currentLanguage$.next(cachedLang);
      if (this.isValidLanguage(cachedLang)) {
        this.transloco.setActiveLang(cachedLang);
      } else {
        this.transloco.setActiveLang(defaultLang);
      }
      
      return of(cachedLang);
    }

    if (this.isProduction()) {
      this._languageInitialized = true;
      return of(this.applyLanguage(defaultLang));
    }

    const accessToken = this._extractAccessToken(this._currentUserData);
    
    if (!accessToken) {
      this._languageInitialized = true;
      const fallbackLang = this.applyLanguage(defaultLang);
      sessionStorage.setItem(this.LANGUAGE_CACHE_KEY, fallbackLang);
      return of(fallbackLang);
    }

    const headers: { [key: string]: string } = {
      accept: 'application/vnd.api+json',
      'content-type': 'application/json',
      clientType: 'INTERNAL_MOBILE',
      authorization: `Bearer ${accessToken}`,
    };

    return this.http.get<EvidenceSessionData>(
      this.sessionEndpoint,
      { withCredentials: true, headers }
    ).pipe(
      map(session => {
        const selectedLang = this.selectLanguage(session);
        this._languageInitialized = true;
        sessionStorage.setItem(this.LANGUAGE_CACHE_KEY, selectedLang);
        return selectedLang;
      }),
      catchError(() => {
        this._languageInitialized = true;
        const fallbackLang = this.applyLanguage(defaultLang);
        sessionStorage.setItem(this.LANGUAGE_CACHE_KEY, fallbackLang);
        return of(fallbackLang);
      })
    );
  }
  
  clearCache(): void {
    this._languageInitialized = false;
    sessionStorage.removeItem(this.LANGUAGE_CACHE_KEY);
  }

  public setLocale(locale: string): void {
    this._currentLanguage$.next(locale);
    sessionStorage.setItem(this.LANGUAGE_CACHE_KEY, locale);
    if (this.isValidLanguage(locale)) {
      this.transloco.setActiveLang(locale);
    }
  }

  public getCurrentLocale(): string {
    return this._currentLanguage$.value;
  }

  private isProduction(): boolean {
    const env = (ENV as string).toLowerCase();
    return env.includes('prod') || env.includes('production');
  }

  private selectLanguage(session: EvidenceSessionData): string {
    const defaultLang = this.transloco.getDefaultLang();
    const userLang = session.data?.user?.attributes?.language;
    const agencyLang = session.data?.agency?.attributes?.language;
    
    if (userLang) {
      const matchedLang = this.findMatchingLanguage(userLang);
      if (matchedLang) {
        return this.applyLanguage(matchedLang);
      }
      return this.applyLanguageForFormatting(userLang, defaultLang);
    }
    
    if (agencyLang) {
      const matchedLang = this.findMatchingLanguage(agencyLang);
      if (matchedLang) {
        return this.applyLanguage(matchedLang);
      }
      return this.applyLanguageForFormatting(agencyLang, defaultLang);
    }
    
    return this.applyLanguage(defaultLang);
  }

  private applyLanguage(language: string): string {
    this.transloco.setActiveLang(language);
    this._currentLanguage$.next(language);
    return language;
  }

  private applyLanguageForFormatting(formattingLocale: string, translationFallback: string): string {
    // Validate that the locale is at least in a valid format (e.g., 'de', 'de-DE')
    // If invalid (like 'e'), fall back to translation default for both
    const isValidFormat = /^[a-z]{2}(-[A-Z]{2})?$/i.test(formattingLocale);
    
    if (isValidFormat) {
      this._currentLanguage$.next(formattingLocale);
      this.transloco.setActiveLang(translationFallback);
      return formattingLocale;
    } else {
      this.transloco.setActiveLang(translationFallback);
      this._currentLanguage$.next(translationFallback);
      return translationFallback;
    }
  }

  private findMatchingLanguage(langCode: string | undefined): string | undefined {
    if (!langCode) return undefined;

    const langs: AvailableLangs = this.transloco.getAvailableLangs();
    const available: string[] = this._normalizeAvailableLangs(langs);
    const lowerCode = langCode.toLowerCase();

    const variations = [
      lowerCode,
      lowerCode.replace('_', '-'),
      lowerCode.replace('-', '_'),
    ];

    for (const variant of variations) {
      const match = available.find((lang) => lang.toLowerCase() === variant);
      if (match) {
        return match;
      }
    }

    if (!lowerCode.includes('-') && !lowerCode.includes('_')) {
      const partialMatch = available.find((lang) => {
        const langLower = lang.toLowerCase();
        return (
          langLower === lowerCode ||
          langLower.startsWith(lowerCode + '-') ||
          langLower.startsWith(lowerCode + '_')
        );
      });

      if (partialMatch) {
        return partialMatch;
      }
    }

    return undefined;
  }

  private isValidLanguage(lang: string): boolean {
    const availableLangs = this._normalizeAvailableLangs(this.transloco.getAvailableLangs());
    return availableLangs.includes(lang);
  }

  private _normalizeAvailableLangs(langs: AvailableLangs): string[] {
    if (!Array.isArray(langs) || langs.length === 0) {
      return [];
    }
    const first: any = langs[0];
    if (typeof first === 'string') {
      return langs as string[];
    }
    return (langs as Array<{ id: string; label: string }>).map((l) => l.id);
  }

  private get sessionEndpoint(): string {
    return `https://livestream${AXON_EDC_URL}/api/v1/session`;
  }


  private _extractAccessToken(currentUserData?: SnCurrentUser | string): string | undefined {
    if (!currentUserData) return undefined;
    if (typeof currentUserData === 'string') return currentUserData || undefined;
    return currentUserData.settings?.authTokens?.axonconnect?.accessToken || undefined;
  }
}